import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, TrendingUp, MessageSquare, FileText, Handshake } from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
  PieChart, Pie, Cell, Legend, LineChart, Line, FunnelChart, Funnel, LabelList,
} from "recharts";

const COLORS = ["#D4AF37", "#0B1320", "#10B981", "#6366F1", "#EF4444", "#F59E0B", "#3B82F6", "#94A3B8"];

export default function Analytics() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState(null);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const path = user.role === "creator" ? "/api/analytics/creator" : "/api/analytics/sponsor";
        const [{ data: main }, { data: tl }] = await Promise.all([
          api.get(path),
          api.get("/api/analytics/timeline").catch(() => ({ data: null })),
        ]);
        setData(main);
        setTimeline(tl);
      } catch (e) {
        console.error("analytics_load_failed", e);
        toast.error(t("analytics.toast_load_failed"));
      }
    })();
  }, [user, t]);

  if (!user) return null;

  return (
    <DashboardLayout
      title={t("analytics.title")}
      subtitle={user.role === "creator" ? t("analytics.subtitle_creator") : t("analytics.subtitle_sponsor")}
    >
      {!data ? (
        <div className="text-slate-500">{t("analytics.loading")}</div>
      ) : user.role === "creator" ? (
        <CreatorAnalytics data={data} timeline={timeline} />
      ) : (
        <SponsorAnalytics data={data} timeline={timeline} />
      )}
    </DashboardLayout>
  );
}

function Kpi({ label, value, icon: Icon }) {
  return (
    <div className="kpi-card p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">{label}</p>
          <p className="text-3xl font-display text-petroleum mt-2">{value ?? 0}</p>
        </div>
        <span className="inline-flex h-10 w-10 rounded-full items-center justify-center bg-gold/10 text-gold">
          <Icon size={18} />
        </span>
      </div>
    </div>
  );
}

function TimelineCard({ timeline }) {
  const { t } = useTranslation();
  if (!timeline?.days?.length) return null;
  return (
    <div className="kpi-card p-6" data-testid="analytics-timeline">
      <h3 className="section-title mb-1">{t("analytics.timeline_title")}</h3>
      <p className="text-xs text-slate-500 mb-4">{t("analytics.timeline_desc")}</p>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={timeline.days} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="day" stroke="#64748b" tick={{ fontSize: 11 }} interval={4} />
          <YAxis stroke="#64748b" tick={{ fontSize: 12 }} allowDecimals={false} />
          <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
          <Legend />
          <Line type="monotone" dataKey="contacts" name={t("analytics.series_contacts")} stroke="#0B1320" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="proposals" name={t("analytics.series_proposals")} stroke="#D4AF37" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey="messages" name={t("analytics.series_messages")} stroke="#10B981" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CreatorAnalytics({ data, timeline }) {
  const { t } = useTranslation();
  const [drill, setDrill] = useState(null);
  const [loadingDrill, setLoadingDrill] = useState(null);

  const openDrill = async (title) => {
    const proj = (data.per_project || []).find((p) => p.title === title);
    if (!proj) return;
    const mine = await api.get("/api/projects/mine");
    const match = mine.data.find((p) => p.title === title);
    if (!match) return;
    setLoadingDrill(title);
    try {
      const { data: payload } = await api.get(`/api/analytics/project/${match.id}`);
      setDrill(payload);
    } catch (e) {
      console.error("analytics_drill_failed", e);
      toast.error(t("analytics.toast_drill_failed"));
    }
    setLoadingDrill(null);
  };

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Kpi label={t("analytics.kpi_projects")} value={data.total_projects} icon={FileText} />
        <Kpi label={t("analytics.kpi_views")} value={data.total_views} icon={TrendingUp} />
        <Kpi label={t("analytics.kpi_saves")} value={data.total_saves} icon={BarChart3} />
        <Kpi label={t("analytics.kpi_contacts")} value={data.contacts_received} icon={MessageSquare} />
        <Kpi label={t("analytics.kpi_rooms")} value={data.rooms} icon={Handshake} />
      </div>

      {timeline && <div className="mb-6"><TimelineCard timeline={timeline} /></div>}

      <div className="kpi-card p-6">
        <div className="flex items-center justify-between mb-1">
          <h3 className="section-title">{t("analytics.per_project_title")}</h3>
          <p className="text-[10px] uppercase tracking-widest text-slate-400">{t("analytics.click_bar_hint")}</p>
        </div>
        {data.per_project?.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.per_project} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="title" stroke="#64748b" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" interval={0} height={60} />
              <YAxis stroke="#64748b" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Legend />
              <Bar
                dataKey="views" fill="#0B1320" name={t("analytics.views_label")} radius={[6, 6, 0, 0]}
                onClick={(entry) => openDrill(entry.title)}
                cursor="pointer"
              />
              <Bar
                dataKey="saves" fill="#D4AF37" name={t("analytics.saves_label")} radius={[6, 6, 0, 0]}
                onClick={(entry) => openDrill(entry.title)}
                cursor="pointer"
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-slate-500 text-sm">{t("analytics.no_data")}</p>
        )}
      </div>

      {loadingDrill && (
        <div className="fixed bottom-6 right-6 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs text-slate-500 shadow z-50">
          {t("analytics.loading_project", { name: loadingDrill })}
        </div>
      )}

      <Dialog open={!!drill} onOpenChange={(v) => !v && setDrill(null)}>
        <DialogContent className="max-w-2xl" data-testid="analytics-drilldown">
          <DialogHeader>
            <DialogTitle>{t("analytics.drill_title", { title: drill?.project?.title || "" })}</DialogTitle>
          </DialogHeader>
          {drill && (
            <div className="space-y-6 py-2">
              <div className="grid grid-cols-5 gap-3">
                <MiniKpi label={t("analytics.mini_views")} value={drill.views} />
                <MiniKpi label={t("analytics.mini_saves")} value={drill.saves} />
                <MiniKpi label={t("analytics.mini_contacts")} value={drill.contacts} />
                <MiniKpi label={t("analytics.mini_proposals")} value={drill.proposals} />
                <MiniKpi label={t("analytics.mini_rooms")} value={drill.rooms} />
              </div>
              <div>
                <p className="eyebrow text-gold mb-2 text-[10px]">{t("analytics.funnel_title")}</p>
                <ResponsiveContainer width="100%" height={220}>
                  <FunnelChart>
                    <Tooltip />
                    <Funnel dataKey="value" data={drill.funnel} isAnimationActive labelLine={false}>
                      {drill.funnel.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      <LabelList position="right" fill="#0B1320" stroke="none" dataKey="stage" fontSize={12} />
                      <LabelList position="center" fill="#fff" stroke="none" dataKey="value" fontSize={12} />
                    </Funnel>
                  </FunnelChart>
                </ResponsiveContainer>
              </div>
              {drill.proposals_by_status && Object.keys(drill.proposals_by_status).length > 0 && (
                <div>
                  <p className="eyebrow text-gold mb-2 text-[10px]">{t("analytics.proposals_by_status")}</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(drill.proposals_by_status).map(([k, v]) => (
                      <span key={k} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                        {k}: <b>{v}</b>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function MiniKpi({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 p-3 text-center">
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="text-2xl font-display text-petroleum mt-1">{value ?? 0}</p>
    </div>
  );
}

function SponsorAnalytics({ data, timeline }) {
  const { t } = useTranslation();
  const statusData = Object.entries(data.by_status || {}).map(([k, v]) => ({ name: k, value: v }));
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <Kpi label={t("analytics.kpi_briefs")} value={data.briefs} icon={FileText} />
        <Kpi label={t("analytics.kpi_active_briefs")} value={data.active_briefs} icon={TrendingUp} />
        <Kpi label={t("analytics.kpi_contacts_sent")} value={data.contacts_sent} icon={MessageSquare} />
        <Kpi label={t("analytics.kpi_proposals")} value={data.proposals_sent + data.proposals_received} icon={BarChart3} />
        <Kpi label={t("analytics.kpi_rooms")} value={data.rooms} icon={Handshake} />
      </div>

      {timeline && <div className="mb-6"><TimelineCard timeline={timeline} /></div>}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="kpi-card p-6">
          <h3 className="section-title mb-4">{t("analytics.by_status_title")}</h3>
          {statusData.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" outerRadius={100} label>
                  {statusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-slate-500 text-sm">{t("analytics.no_proposals")}</p>
          )}
        </div>

        <div className="kpi-card p-6">
          <h3 className="section-title mb-4">{t("analytics.activity_title")}</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={[
              { name: t("analytics.activity_contacts"), v: data.contacts_sent },
              { name: t("analytics.activity_prop_sent"), v: data.proposals_sent },
              { name: t("analytics.activity_prop_received"), v: data.proposals_received },
              { name: t("analytics.activity_rooms"), v: data.rooms },
            ]}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11 }} />
              <YAxis stroke="#64748b" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="v" fill="#0B1320" name={t("analytics.activity_count")} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );
}
